//@flow
import React, {useState} from 'react';

import {makeStyles, MenuItem, TableCell} from '@material-ui/core';
import PillSelect from '../../../../../elements/PillSelect';
import {colors} from '../../../../../assets/styles/colors';
import {useProductContext} from '../../../context';

import {useEditProduct} from '../../../utils/editProduct';

import type {BulkEditProductSubCategory} from '../../../../../entities';
import {useCategories} from '../../../../categories/utils';

const useStyles = makeStyles(theme => ({
  selectField: {
    whiteSpace: 'nowrap',
    padding: '15px',
    borderRadius: '8px',
    height: '48px',
    color: colors.grey800,
    border: `1px solid ${colors.grey300}`,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectLabel: {
    whiteSpace: 'nowrap',
    maxWidth: '18ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  filterItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '250px',
    borderBottom: `1px solid ${colors.grey200}`,
    padding: '0.75rem 1.5rem',

    '&:last-child': {
      borderBottom: 'none',
    },

    '&[data-disabled="true"]': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },
  filterLabel: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: '.9rem',
  },
  customCheckbox: {
    borderRadius: '50%',
    width: '1.5rem',
    height: '1.5rem',
    border: `1.25px solid ${colors.grey400}`,
    background: 'white',

    '&[data-checked="true"]': {
      border: `6px solid ${colors.primary}`,
    },
  },
  categoryName: {
    color: colors.purple,
    fontWeight: 600,
    fontSize: '1.125rem',
    maxWidth: '18ch',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    padding: '0.75rem 1rem',
  },
}));

export default function SubCategoryCell() {
  const classes = useStyles();
  const [product] = useProductContext(product => ({
    id: product.id,
    subCategories: product.subCategories,
    categories: product.categories || [],
  }));

  const categories = useCategories();

  const {editProduct} = useEditProduct(product ? product.id : '');
  const categoriesSelected = product.categories;

  const [subCategoriesSelected, setSubCategoriesSelected] = useState<
    BulkEditProductSubCategory[]
  >(product && product.subCategories ? product.subCategories : []);

  if (!product) return null;

  const categoriesWithSubCategories = categories.filter(category => {
    return (
      categoriesSelected.some(c => c.id === category.id) &&
      category.subCategories &&
      category.subCategories.length > 0
    );
  });

  const disabled = categoriesWithSubCategories.length === 0;

  const label = subCategoriesSelected
    .map(subCategory => subCategory.name)
    .join(', ');

  const toggleSubCategory = (subCategory: BulkEditProductSubCategory) => {
    const selected = subCategoriesSelected.some(
      selectedSubCategory => selectedSubCategory.id === subCategory.id
    );

    if (selected) {
      setSubCategoriesSelected(
        subCategoriesSelected.filter(
          selectedSubCategory => selectedSubCategory.id !== subCategory.id
        )
      );
      return;
    }
    setSubCategoriesSelected([...subCategoriesSelected, subCategory]);
  };

  const saveSubCategories = () => {
    editProduct({
      subCategories: subCategoriesSelected.map(subCategory => subCategory.id),
    });
  };

  return (
    <TableCell align="right">
      <PillSelect
        label={label || 'Sub Categorias'}
        variant="outlined"
        disabled={disabled}
        onClose={saveSubCategories}
        classes={{
          button: classes.selectField,
          label: classes.selectLabel,
        }}
      >
        {categoriesWithSubCategories.map(category => {
          return (
            <div key={category.id}>
              <header className={classes.categoryName}>{category.name}</header>
              {category.subCategories &&
                category.subCategories.map((subCategory, index) => {
                  const selected = subCategoriesSelected.some(
                    selectedSubCategory =>
                      selectedSubCategory.id === subCategory.id
                  );

                  return (
                    <MenuItem
                      key={subCategory.id}
                      className={classes.filterItem}
                      onClick={() => toggleSubCategory(subCategory)}
                    >
                      <span className={classes.filterLabel}>
                        {subCategory.name}
                      </span>
                      <span
                        className={classes.customCheckbox}
                        data-checked={selected}
                      />
                    </MenuItem>
                  );
                })}
            </div>
          );
        })}
      </PillSelect>
    </TableCell>
  );
}
